import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ChatbotSettings.css';
import './TrackingSettings.css';
import { useTranslation } from 'react-i18next';
import {
    FaSpinner,
    FaCheck,
    FaTimes,
    FaExclamationTriangle,
    FaUpload,
    FaImage,
    FaBuilding,
    FaPalette,
    FaRocket,
    FaChevronDown,
    FaChevronRight,
    FaTrash,
    FaEllipsisH,
    FaCopy,
    FaCommentDots,
} from 'react-icons/fa';
import TrackingPreview from './TrackingPreview';

/* Tracking page settings v2 (2026-10-06 redesign): one brand color with
   derived palette, uploads instead of pasted URLs, no custom CSS, faithful
   live preview. Mirrors the ChatbotSettings v2 design system (cs-*). */

const THEME_PRESETS = ['#C47A4A', '#3FBDB4', '#4A6CC4', '#1F2937', '#B04A5A', '#3E7D52'];

const contrastRatio = (hex) => {
    if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
    const channel = (v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const r = channel(parseInt(hex.slice(1, 3), 16));
    const g = channel(parseInt(hex.slice(3, 5), 16));
    const b = channel(parseInt(hex.slice(5, 7), 16));
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const yiq = (parseInt(hex.slice(1, 3), 16) * 299 + parseInt(hex.slice(3, 5), 16) * 587 + parseInt(hex.slice(5, 7), 16) * 114) / 1000;
    const textL = yiq >= 128 ? 0 : 1;
    const lighter = Math.max(L, textL);
    const darker = Math.min(L, textL);
    return (lighter + 0.05) / (darker + 0.05);
};

const DEFAULT_FORM = {
    colors: {
        primary_color: '#2d2d2d',
        secondary_color: '#f8f9fa',
        background_color: '#ffffff',
        text_color: '#333333',
        border_color: '#e0e0e0',
        success_color: '#28a745',
        warning_color: '#ffc107',
        error_color: '#dc3545',
    },
    banner: {
        enabled: true,
        title: '',
        subtitle: '',
        background_image: '',
        background_image_key: null,
        background_color: '#f8f9fa',
        text_color: '#333333',
        height: 200,
    },
    branding: {
        company_name: '',
        company_logo: '',
        logo_key: null,
        show_powered_by: true,
    },
};

const TrackingSettings = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [settings, setSettings] = useState(null);
    const [brands, setBrands] = useState([]);
    const [selectedBrandId, setSelectedBrandId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [previewMode, setPreviewMode] = useState('search');
    const [logoUploading, setLogoUploading] = useState(false);
    const [bannerUploading, setBannerUploading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const logoInputRef = useRef(null);
    const bannerInputRef = useRef(null);

    const [expandedSections, setExpandedSections] = useState({
        identity: true,
        theme: true,
        welcome: false,
        deploy: false,
    });

    const [formData, setFormData] = useState(DEFAULT_FORM);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const userData = await fetchUserData(token);
                await fetchSettings(token);
                if (userData?.has_brand) {
                    await fetchBrands(token);
                }
            } catch (err) {
                console.error('Error loading tracking settings data:', err);
                setError(t('trackingSettingsV2.loadError', 'Impossible de charger les paramètres de suivi.'));
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, selectedBrandId]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(response.data);
            return response.data;
        } catch (err) {
            console.error('Failed to fetch user data:', err);
            setUser(null);
            navigate('/');
            return null;
        }
    };

    const fetchBrands = async (token) => {
        try {
            const response = await axios.get('/api/brands/list', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBrands(response.data || []);
        } catch (err) {
            console.error('Failed to fetch brands:', err);
            setBrands([]);
        }
    };

    const mergeForm = (data) => ({
        colors: { ...DEFAULT_FORM.colors, ...(data.colors || {}) },
        banner: { ...DEFAULT_FORM.banner, ...(data.banner || {}) },
        branding: { ...DEFAULT_FORM.branding, ...(data.branding || {}) },
    });

    const fetchSettings = async (token) => {
        try {
            const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
            const response = await axios.get('/api/tracking_settings/settings', {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            setSettings(response.data);
            setFormData(mergeForm(response.data));
            setHasUnsavedChanges(false);
        } catch (err) {
            console.error('Failed to fetch tracking settings:', err);
            setSettings(null);
            setFormData(DEFAULT_FORM);
        }
    };

    const canManageSettings = () => user && ['admin', 'seller', 'eshop'].includes(user.role);

    const getBrandName = (brandId) => {
        if (!brandId) return t('trackingSettingsV2.defaultSettings', 'Paramètres par défaut');
        const brand = brands.find((b) => b.id === brandId);
        return brand ? brand.name : t('trackingSettingsV2.unknownBrand', 'Marque inconnue');
    };

    const handleInputChange = (section, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: { ...prev[section], [field]: value },
        }));
        setHasUnsavedChanges(true);
    };

    const applyPreset = (color) => {
        setFormData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                primary_color: color,
                secondary_color: '#F6F5F2',
                background_color: '#ffffff',
                text_color: '#1F2937',
                border_color: '#E2DCD2',
            },
        }));
        setHasUnsavedChanges(true);
    };

    const handleImageFile = async (kind, file) => {
        if (!file) return;
        const setUploading = kind === 'logo' ? setLogoUploading : setBannerUploading;
        setUploading(true);
        setError(null);
        try {
            const token = localStorage.getItem('access_token');
            const form = new FormData();
            form.append('file', file);
            const response = await axios.post(`/api/tracking_settings/upload?kind=${kind}`, form, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            });
            if (kind === 'logo') {
                setFormData((prev) => ({
                    ...prev,
                    branding: { ...prev.branding, logo_key: response.data.key, company_logo: response.data.url },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    banner: { ...prev.banner, background_image_key: response.data.key, background_image: response.data.url },
                }));
            }
            setHasUnsavedChanges(true);
        } catch (err) {
            console.error('Image upload failed:', err);
            setError(err.response?.data?.detail
                || t('trackingSettingsV2.uploadError', 'Envoi impossible (PNG, JPEG ou WebP uniquement).'));
        } finally {
            setUploading(false);
        }
    };

    const removeImage = (kind) => {
        if (kind === 'logo') {
            setFormData((prev) => ({ ...prev, branding: { ...prev.branding, logo_key: null, company_logo: '' } }));
        } else {
            setFormData((prev) => ({ ...prev, banner: { ...prev.banner, background_image_key: null, background_image: '' } }));
        }
        setHasUnsavedChanges(true);
    };

    const handleSave = async () => {
        if (!canManageSettings()) return;
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        const token = localStorage.getItem('access_token');
        try {
            const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
            await axios.put('/api/tracking_settings/settings', formData, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            setSuccess(t('trackingSettingsV2.saved', 'Paramètres de suivi enregistrés.'));
            await fetchSettings(token);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Failed to update tracking settings:', err);
            setError(err.response?.data?.detail
                || t('trackingSettingsV2.saveError', 'Enregistrement impossible. Réessayez.'));
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        const token = localStorage.getItem('access_token');
        try {
            const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
            await axios.delete('/api/tracking_settings/settings', {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            setSuccess(t('trackingSettingsV2.deleted', 'Paramètres réinitialisés.'));
            await fetchSettings(token);
            setIsDeleteModalOpen(false);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Failed to delete tracking settings:', err);
            setError(err.response?.data?.detail
                || t('trackingSettingsV2.deleteError', 'Suppression impossible. Réessayez.'));
        } finally {
            setIsDeleting(false);
        }
    };

    const tenantId = user ? (user.role === 'employee' ? user.admin_id : user._id) : '';
    const embedCode = `<script src="https://unanimity.ai/api/trackingStatic/tracking_page.js" data-user-id="${tenantId || ''}"${selectedBrandId ? ` data-brand-id="${selectedBrandId}"` : ''}></script>`;

    const handleCopyEmbed = async () => {
        try {
            await navigator.clipboard.writeText(embedCode);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
            console.error('Clipboard copy failed:', err);
        }
    };

    const toggleSection = (key) => {
        setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const renderSectionHeader = (key, icon, label) => (
        <button type="button" className="cs-section-header" onClick={() => toggleSection(key)}>
            <div className="cs-section-header-left">
                <span className="cs-section-icon">{icon}</span>
                <span>{label}</span>
            </div>
            {expandedSections[key] ? <FaChevronDown /> : <FaChevronRight />}
        </button>
    );

    const renderColorField = (name, label) => (
        <div className="cs-color-row" key={name}>
            <label htmlFor={`tracking_${name}`}>{label}</label>
            <div className="cs-color-input-group">
                <input
                    type="color"
                    id={`tracking_${name}`}
                    value={formData.colors[name] || '#000000'}
                    onChange={(e) => handleInputChange('colors', name, e.target.value)}
                    className="cs-color-picker"
                />
                <input
                    type="text"
                    value={formData.colors[name] || ''}
                    onChange={(e) => handleInputChange('colors', name, e.target.value)}
                    className="cs-color-hex"
                />
            </div>
        </div>
    );

    const ratio = contrastRatio(formData.colors.primary_color);
    const contrastOk = ratio !== null && ratio >= 4.5;

    if (isLoading) {
        return (
            <div className="cs-loading">
                <FaSpinner className="cs-spinner" />
                <p>{t('trackingSettingsV2.loading', 'Chargement des paramètres de suivi…')}</p>
            </div>
        );
    }

    return (
        <div className="cs-container ts-container">
            <div className="cs-header">
                <div className="cs-header-left">
                    <h1 className="cs-title">{t('trackingSettingsV2.title', 'Page de suivi')}</h1>
                    {hasUnsavedChanges && (
                        <span className="cs-unsaved-badge">
                            {t('trackingSettingsV2.unsaved', 'Modifications non enregistrées')}
                        </span>
                    )}
                </div>
                <div className="cs-header-actions">
                    {canManageSettings() && (
                        <div className="ts-menu-wrapper">
                            <button
                                type="button"
                                className="cs-btn cs-btn-outline"
                                aria-label={t('trackingSettingsV2.moreActions', 'Plus d’actions')}
                                onClick={() => setMenuOpen((open) => !open)}
                            >
                                <FaEllipsisH />
                            </button>
                            {menuOpen && (
                                <div className="ts-menu">
                                    <button
                                        type="button"
                                        className="ts-menu-item ts-menu-danger"
                                        onClick={() => { setMenuOpen(false); setIsDeleteModalOpen(true); }}
                                    >
                                        <FaTrash /> {t('trackingSettingsV2.deleteSettings', 'Supprimer les paramètres')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    <button
                        type="button"
                        className="cs-btn cs-btn-primary"
                        onClick={handleSave}
                        disabled={isSaving || !canManageSettings()}
                    >
                        {isSaving
                            ? <><FaSpinner className="cs-spinner" /> {t('trackingSettingsV2.saving', 'Enregistrement…')}</>
                            : t('trackingSettingsV2.save', 'Save')}
                    </button>
                </div>
            </div>

            {error && (
                <div className="cs-notification cs-notification-error">
                    <span>{error}</span>
                    <button type="button" className="cs-notification-close" onClick={() => setError(null)}>×</button>
                </div>
            )}
            {success && (
                <div className="cs-notification cs-notification-success">
                    <span>{success}</span>
                    <button type="button" className="cs-notification-close" onClick={() => setSuccess(null)}>×</button>
                </div>
            )}

            <div className="cs-main">
                <div className="cs-settings-panel">
                    {/* Identité */}
                    <div className="cs-section">
                        {renderSectionHeader('identity', <FaBuilding />, t('trackingSettingsV2.sectionIdentity', 'Identité'))}
                        {expandedSections.identity && (
                            <div className="cs-section-body">
                                {user?.has_brand && brands.length > 0 && (
                                    <div className="cs-field">
                                        <label htmlFor="tracking-brand-select">{t('trackingSettingsV2.brand', 'Marque')}</label>
                                        <select
                                            id="tracking-brand-select"
                                            value={selectedBrandId}
                                            onChange={(e) => setSelectedBrandId(e.target.value)}
                                        >
                                            <option value="">{t('trackingSettingsV2.defaultSettings', 'Paramètres par défaut')}</option>
                                            {brands.map((brand) => (
                                                <option key={brand.id} value={brand.id}>{brand.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div className="cs-field">
                                    <label>{t('trackingSettingsV2.logo', 'Logo')}</label>
                                    <div
                                        className="cs-logo-drop"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => logoInputRef.current?.click()}
                                        onKeyDown={(e) => { if (e.key === 'Enter') logoInputRef.current?.click(); }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => { e.preventDefault(); handleImageFile('logo', e.dataTransfer.files?.[0]); }}
                                    >
                                        {formData.branding.company_logo ? (
                                            <img src={formData.branding.company_logo} alt="" className="cs-logo-thumb" />
                                        ) : (
                                            <span className="cs-logo-placeholder">
                                                {(formData.branding.company_name || '?').charAt(0).toUpperCase()}
                                            </span>
                                        )}
                                        <span className="cs-logo-hint">
                                            {logoUploading
                                                ? <><FaSpinner className="cs-spinner" /> {t('trackingSettingsV2.uploading', 'Envoi…')}</>
                                                : <><FaUpload /> {t('trackingSettingsV2.logoHint', 'Glissez un logo (PNG, JPEG, WebP) — affiché dans l’en-tête de la page')}</>}
                                        </span>
                                        {formData.branding.company_logo && (
                                            <button
                                                type="button"
                                                className="cs-logo-remove"
                                                onClick={(e) => { e.stopPropagation(); removeImage('logo'); }}
                                                aria-label={t('trackingSettingsV2.logoRemove', 'Retirer le logo')}
                                            >
                                                <FaTimes />
                                            </button>
                                        )}
                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept=".png,.jpg,.jpeg,.webp"
                                            hidden
                                            onChange={(e) => { handleImageFile('logo', e.target.files?.[0]); e.target.value = ''; }}
                                        />
                                    </div>
                                </div>
                                <div className="cs-field">
                                    <label htmlFor="tracking_company_name">{t('trackingSettingsV2.companyName', 'Nom de l’entreprise')}</label>
                                    <input
                                        type="text"
                                        id="tracking_company_name"
                                        value={formData.branding.company_name}
                                        onChange={(e) => handleInputChange('branding', 'company_name', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Thème */}
                    <div className="cs-section">
                        {renderSectionHeader('theme', <FaPalette />, t('trackingSettingsV2.sectionTheme', 'Thème'))}
                        {expandedSections.theme && (
                            <div className="cs-section-body">
                                <div className="cs-field">
                                    <label>{t('trackingSettingsV2.brandColor', 'Couleur de marque')}</label>
                                    <div className="cs-swatches">
                                        {THEME_PRESETS.map((color) => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`cs-swatch${formData.colors.primary_color.toUpperCase() === color.toUpperCase() ? ' active' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => applyPreset(color)}
                                                aria-label={color}
                                            />
                                        ))}
                                        <div className="cs-swatch-custom" title={t('trackingSettingsV2.customColor', 'Couleur personnalisée')}>
                                            <input
                                                type="color"
                                                value={formData.colors.primary_color}
                                                onChange={(e) => applyPreset(e.target.value)}
                                                aria-label={t('trackingSettingsV2.customColor', 'Couleur personnalisée')}
                                            />
                                        </div>
                                    </div>
                                    <p className={`cs-contrast ${contrastOk ? 'ok' : 'warn'}`}>
                                        {contrastOk
                                            ? <><FaCheck /> {t('trackingSettingsV2.contrastOk', 'Contraste AA vérifié ({{ratio}}:1)', { ratio: ratio ? ratio.toFixed(1) : '—' })}</>
                                            : <><FaExclamationTriangle /> {t('trackingSettingsV2.contrastWarn', 'Contraste faible ({{ratio}}:1) — le texte sera peu lisible', { ratio: ratio ? ratio.toFixed(1) : '—' })}</>}
                                    </p>
                                </div>
                                <details className="cs-advanced">
                                    <summary>{t('trackingSettingsV2.advancedColors', 'Réglages avancés — couleurs séparées')}</summary>
                                    <div className="cs-advanced-body">
                                        {renderColorField('primary_color', t('trackingSettingsV2.primaryColor', 'Couleur principale'))}
                                        {renderColorField('secondary_color', t('trackingSettingsV2.secondaryColor', 'Couleur secondaire'))}
                                        {renderColorField('background_color', t('trackingSettingsV2.backgroundColor', 'Fond des cartes'))}
                                        {renderColorField('text_color', t('trackingSettingsV2.textColor', 'Texte'))}
                                        {renderColorField('border_color', t('trackingSettingsV2.borderColor', 'Bordures'))}
                                        {renderColorField('success_color', t('trackingSettingsV2.successColor', 'Succès'))}
                                        {renderColorField('warning_color', t('trackingSettingsV2.warningColor', 'Avertissement'))}
                                        {renderColorField('error_color', t('trackingSettingsV2.errorColor', 'Erreur'))}
                                    </div>
                                </details>
                            </div>
                        )}
                    </div>

                    {/* Accueil */}
                    <div className="cs-section">
                        {renderSectionHeader('welcome', <FaCommentDots />, t('trackingSettingsV2.sectionWelcome', 'Accueil'))}
                        {expandedSections.welcome && (
                            <div className="cs-section-body">
                                <div className="cs-field">
                                    <label htmlFor="tracking_banner_title">{t('trackingSettingsV2.heroTitle', 'Titre')}</label>
                                    <input
                                        type="text"
                                        id="tracking_banner_title"
                                        value={formData.banner.title}
                                        onChange={(e) => handleInputChange('banner', 'title', e.target.value)}
                                        maxLength={80}
                                        placeholder={t('trackingSettingsV2.heroTitlePlaceholder', 'Suivez votre colis')}
                                    />
                                </div>
                                <div className="cs-field">
                                    <label htmlFor="tracking_banner_subtitle">{t('trackingSettingsV2.heroSubtitle', 'Sous-titre')}</label>
                                    <input
                                        type="text"
                                        id="tracking_banner_subtitle"
                                        value={formData.banner.subtitle}
                                        onChange={(e) => handleInputChange('banner', 'subtitle', e.target.value)}
                                        maxLength={140}
                                        placeholder={t('trackingSettingsV2.heroSubtitlePlaceholder', 'Entrez votre e-mail pour suivre vos commandes en temps réel.')}
                                    />
                                </div>
                                <div className="cs-field">
                                    <label>{t('trackingSettingsV2.heroImage', 'Image d’en-tête (optionnelle)')}</label>
                                    <div
                                        className="cs-logo-drop ts-banner-drop"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => bannerInputRef.current?.click()}
                                        onKeyDown={(e) => { if (e.key === 'Enter') bannerInputRef.current?.click(); }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => { e.preventDefault(); handleImageFile('banner', e.dataTransfer.files?.[0]); }}
                                    >
                                        {formData.banner.background_image ? (
                                            <img src={formData.banner.background_image} alt="" className="ts-banner-thumb" />
                                        ) : (
                                            <span className="cs-logo-placeholder"><FaImage /></span>
                                        )}
                                        <span className="cs-logo-hint">
                                            {bannerUploading
                                                ? <><FaSpinner className="cs-spinner" /> {t('trackingSettingsV2.uploading', 'Envoi…')}</>
                                                : <><FaUpload /> {t('trackingSettingsV2.heroImageHint', 'Glissez une image — assombrie automatiquement pour garder le titre lisible')}</>}
                                        </span>
                                        {formData.banner.background_image && (
                                            <button
                                                type="button"
                                                className="cs-logo-remove"
                                                onClick={(e) => { e.stopPropagation(); removeImage('banner'); }}
                                                aria-label={t('trackingSettingsV2.heroImageRemove', 'Retirer l’image')}
                                            >
                                                <FaTimes />
                                            </button>
                                        )}
                                        <input
                                            ref={bannerInputRef}
                                            type="file"
                                            accept=".png,.jpg,.jpeg,.webp"
                                            hidden
                                            onChange={(e) => { handleImageFile('banner', e.target.files?.[0]); e.target.value = ''; }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Déploiement */}
                    <div className="cs-section">
                        {renderSectionHeader('deploy', <FaRocket />, t('trackingSettingsV2.sectionDeploy', 'Déploiement'))}
                        {expandedSections.deploy && (
                            <div className="cs-section-body">
                                <div className="cs-field">
                                    <label>{t('trackingSettingsV2.embedCode', 'Code d’intégration')}</label>
                                    <div className="ts-embed">
                                        <code>{embedCode}</code>
                                        <button type="button" className="cs-btn cs-btn-outline" onClick={handleCopyEmbed}>
                                            {linkCopied
                                                ? <><FaCheck /> {t('trackingSettingsV2.copied', 'Copié')}</>
                                                : <><FaCopy /> {t('trackingSettingsV2.copy', 'Copier')}</>}
                                        </button>
                                    </div>
                                    <p className="ts-hint">
                                        {t('trackingSettingsV2.embedHint', 'Collez ce script sur la page de votre site où le suivi doit apparaître.')}
                                    </p>
                                </div>
                                <div className="cs-field">
                                    <div className="cs-toggle-wrapper">
                                        <input
                                            type="checkbox"
                                            id="tracking_show_powered_by"
                                            checked={formData.branding.show_powered_by}
                                            onChange={(e) => handleInputChange('branding', 'show_powered_by', e.target.checked)}
                                            className="cs-toggle-input"
                                        />
                                        <label htmlFor="tracking_show_powered_by" className="cs-toggle-track">
                                            <span className="cs-toggle-thumb" />
                                        </label>
                                        <label htmlFor="tracking_show_powered_by">
                                            {t('trackingSettingsV2.showPoweredBy', 'Afficher « Propulsé par Unanimity AI »')}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="cs-preview-panel">
                    <div className="cs-preview-modes" role="group" aria-label={t('trackingSettingsV2.previewModes', 'Aperçu')}>
                        {[
                            ['search', t('trackingSettingsV2.modeSearch', 'Recherche')],
                            ['result', t('trackingSettingsV2.modeResult', 'Résultat')],
                            ['desktop', t('trackingSettingsV2.modeDesktop', 'Bureau')],
                        ].map(([mode, label]) => (
                            <button
                                key={mode}
                                type="button"
                                className={`cs-preview-mode${previewMode === mode ? ' active' : ''}`}
                                aria-pressed={previewMode === mode}
                                onClick={() => setPreviewMode(mode)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <TrackingPreview settings={formData} mode={previewMode} t={t} />
                </div>
            </div>

            {isDeleteModalOpen && (
                <div className="una-modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
                    <div className="una-modal-content una-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="una-delete-warning">
                            <FaExclamationTriangle className="una-warning-icon" />
                            <h2>{t('trackingSettingsV2.deleteTitle', 'Supprimer les paramètres ?')}</h2>
                        </div>
                        <p>
                            {t('trackingSettingsV2.deleteConfirmation', 'La page de suivi reviendra à l’apparence par défaut. Cette action est irréversible.')}
                        </p>
                        <p className="una-brand-info">
                            <strong>{t('trackingSettingsV2.brand', 'Marque')} :</strong> {getBrandName(selectedBrandId)}
                        </p>
                        <div className="una-form-actions">
                            <button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="cs-btn cs-btn-secondary"
                            >
                                {t('trackingSettingsV2.cancel', 'Annuler')}
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                className="cs-btn cs-btn-danger-outline"
                                disabled={isDeleting}
                            >
                                {isDeleting
                                    ? <><FaSpinner className="cs-spinner" /> {t('trackingSettingsV2.deleting', 'Suppression…')}</>
                                    : <><FaTrash /> {t('trackingSettingsV2.deleteSettings', 'Supprimer les paramètres')}</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrackingSettings;
